import { useState } from 'react';
import { RefreshCw, Search, X } from 'lucide-react';
import { Spinner } from '@/shared/components/Spinner';
import { formatDateTime } from '@/shared/utils/date';
import { normalizeTraceId } from '@/shared/lib/traceId';
import type { ServerLogLevel } from '@/shared/types/api';
import { LOG_WINDOWS, type LogFilterPatch, type LogFilters as Filters } from './useServerLogs';

/** Mirrors the server's validator (search ≤ 200 chars, source ≤ 200 chars of [A-Za-z0-9_.]). */
const MAX_FILTER_LENGTH = 200;
const SOURCE_PATTERN = /^[A-Za-z0-9_.]*$/;
const CUSTOM_WINDOW = 'custom';

/** Label for an explicit window, so neither bound is ever hidden behind a "Since …". */
function customWindowLabel(from?: string, to?: string): string {
  if (from && to) return `${formatDateTime(from)} – ${formatDateTime(to)}`;
  if (from) return `Since ${formatDateTime(from)}`;
  return `Until ${formatDateTime(to!)}`;
}

const LEVEL_OPTIONS: { value: ServerLogLevel; label: string }[] = [
  { value: 'Verbose', label: 'All levels' },
  { value: 'Debug', label: 'Debug and above' },
  { value: 'Information', label: 'Info and above' },
  { value: 'Warning', label: 'Warning and above' },
  { value: 'Error', label: 'Errors only' },
];

interface LogFiltersProps {
  filters: Filters;
  loading: boolean;
  onChange: (patch: LogFilterPatch) => void;
  onRefresh: () => void;
}

export function LogFilters({ filters, loading, onChange, onRefresh }: LogFiltersProps) {
  const traceScoped = Boolean(filters.traceId);
  // A pasted trace id (from a toast, a response header or a support ticket) applies as soon as it
  // is a full 32-hex id or a W3C traceparent; anything shorter just waits in the box.
  const [traceDraft, setTraceDraft] = useState('');
  const onTraceInput = (value: string) => {
    const traceId = normalizeTraceId(value);
    if (traceId) {
      setTraceDraft('');
      onChange({ traceId });
    } else {
      setTraceDraft(value);
    }
  };

  // The server rejects a source outside [A-Za-z0-9_.] with a 400, which would toast and wipe the
  // table on one stray keystroke. Such a value stays a local, flagged draft and is never sent.
  const [sourceDraft, setSourceDraft] = useState<string | null>(null);
  const onSourceInput = (value: string) => {
    if (SOURCE_PATTERN.test(value.trim())) {
      setSourceDraft(null);
      onChange({ source: value });
    } else {
      setSourceDraft(value);
    }
  };
  const customWindow = Boolean(filters.from || filters.to);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        className="input w-auto"
        aria-label="Minimum level"
        value={filters.minLevel}
        disabled={traceScoped}
        title={traceScoped ? 'Every level is shown while filtering by a trace' : undefined}
        onChange={(e) => onChange({ minLevel: e.target.value })}
      >
        {LEVEL_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <select
        className="input w-auto"
        aria-label="Time window"
        value={customWindow ? CUSTOM_WINDOW : filters.window}
        // Picking a relative window drops an explicit start (e.g. one set by a toast deep link).
        onChange={(e) => onChange({ window: e.target.value, from: undefined, to: undefined })}
      >
        {customWindow && <option value={CUSTOM_WINDOW}>{customWindowLabel(filters.from, filters.to)}</option>}
        {LOG_WINDOWS.map((w) => (
          <option key={w.id} value={w.id}>
            {w.label}
          </option>
        ))}
      </select>

      <div className="relative min-w-48 flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <input
          type="search"
          className="input pl-9"
          aria-label="Search logs"
          placeholder="Search message, exception, source…"
          maxLength={MAX_FILTER_LENGTH}
          value={filters.q}
          onChange={(e) => onChange({ q: e.target.value })}
        />
      </div>

      <input
        className="input w-56 font-mono"
        aria-label="Source"
        placeholder="Source, e.g. FenergoRss.Infrastructure"
        maxLength={MAX_FILTER_LENGTH}
        value={sourceDraft ?? filters.source}
        aria-invalid={sourceDraft !== null || undefined}
        title={sourceDraft !== null ? 'Only letters, digits, "." and "_" are allowed' : undefined}
        onChange={(e) => onSourceInput(e.target.value)}
      />

      {!filters.traceId && (
        <input
          className="input w-56 font-mono"
          aria-label="Trace id"
          placeholder="Trace id (32 hex)"
          maxLength={64}
          value={traceDraft}
          aria-invalid={traceDraft.trim().length > 0 || undefined}
          onChange={(e) => onTraceInput(e.target.value)}
        />
      )}

      {filters.traceId && (
        <span className="chip-tag font-mono" title={filters.traceId}>
          trace {filters.traceId.slice(0, 8)}
          <button
            type="button"
            className="-mr-1 rounded-sm p-0.5 hover:bg-background"
            aria-label="Clear trace filter"
            // The bounds came with the trace (Show trace, a toast link): drop them too, or the list
            // would stay capped at a hidden end time and Refresh would never show newer entries.
            onClick={() => onChange({ traceId: undefined, from: undefined, to: undefined })}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      )}

      <button type="button" onClick={onRefresh} disabled={loading} className="btn-secondary gap-2">
        {loading ? <Spinner /> : <RefreshCw className="h-4 w-4" />}
        Refresh
      </button>
    </div>
  );
}
